import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Grid,
  Typography,
  makeStyles,
  Accordion,
  AccordionDetails,
  AccordionSummary
} from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import { FormikSwitchField } from 'components/form/SwitchField'
import { FormikFileSetField } from 'components/form/FileSetField'
import { FormikMultiSelectDropdownField } from 'components/form/Dropdown'
import { adminFoodStorage, adminInfrastructureAreas } from 'admin/actions/admin_dropdown_options'
import { get } from 'lodash'

const useStyles = makeStyles((theme) => ({
  heading: {
    fontSize: 18,
    fontWeight: 500
  },
  accordion: {
    marginTop: 15
  },
  fieldDescription: {
    fontSize: 12,
    color: 'rgba(0, 0, 0, 0.54)',
    marginTop: -4,
    marginBottom: 4
  }
}))

export default function BeneficiaryVettingHealthSafetyFields({ formik_props, fieldsDisabled = false }) {
  const dispatch = useDispatch()
  const classes = useStyles()
  const [expanded, setExpanded] = React.useState('healthSafety')
  const company = formik_props.values.company || {}

  const foodStorageOptions = useSelector(() => adminFoodStorage.getAsOptions())
  const infrastructureAreasOptions = useSelector(() => adminInfrastructureAreas.getAsOptions())

  useEffect(() => {
    dispatch(adminFoodStorage.fetchListIfNeeded())
    dispatch(adminInfrastructureAreas.fetchListIfNeeded())
  }, [dispatch])

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false)
  }

  return (
    <>
      <Accordion
        expanded={expanded === 'healthSafety'}
        onChange={handleChange('healthSafety')}
        variant="outlined"
        square
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="health-safety-content" id="health-safety-header">
          <Typography className={classes.heading} >
            Health and Safety
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormikMultiSelectDropdownField
                label="Food Storage (dry, chilled, frozen)"
                name="company.food_storage_adequate"
                options={foodStorageOptions || []}
                formik_props={formik_props}
                disabled={fieldsDisabled}
              />
              <Typography className={classes.fieldDescription} variant="caption" display="block">
                Confirm whether the food storage areas for dry, chilled, and frozen items are adequate.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormikFileSetField
                name="company.kitchen_facility_images"
                formik_props={formik_props}
                label="Kitchen & facility photos"
                value={get(company, 'kitchen_facility_images') || []}
                disabled={fieldsDisabled}
              />
              <Typography className={classes.fieldDescription} variant="caption" display="block">
                Upload photos of food preparation and storage areas, fridges, kitchens, and other facilities.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormikSwitchField
                name="company.has_kitchen_cleanliness"
                label="Kitchen Cleanliness / Organisation"
                formik_props={formik_props}
                color="primary"
                checked={get(company, 'has_kitchen_cleanliness')}
                disabled={fieldsDisabled}
              />
              <Typography className={classes.fieldDescription} variant="caption" display="block">
                Indicate whether the kitchen meets cleanliness and organisation standards.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormikSwitchField
                name="company.has_access_to_water"
                label="Access to Water / Cleaning Products"
                formik_props={formik_props}
                color="primary"
                checked={get(company, 'has_access_to_water')}
                disabled={fieldsDisabled}
              />
              <Typography className={classes.fieldDescription} variant="caption" display="block">
                State whether clean water and cleaning materials are available on-site.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormikSwitchField
                name="company.has_hygiene_facilities"
                label="Toilets & Hygiene Facilities"
                formik_props={formik_props}
                color="primary"
                checked={get(company, 'has_hygiene_facilities')}
                disabled={fieldsDisabled}
              />
              <Typography className={classes.fieldDescription} variant="caption" display="block">
                Confirm whether staff/volunteer toilets and hygiene facilities are available and functional.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormikSwitchField
                name="company.is_pest_free"
                label="Pest/Rodent Free"
                formik_props={formik_props}
                color="primary"
                checked={get(company, 'is_pest_free')}
                disabled={fieldsDisabled}
              />
              <Typography className={classes.fieldDescription} variant="caption" display="block">
                Confirm there are no signs of pests or rodents.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormikMultiSelectDropdownField
                name="company.infrastructure_checks_adequate"
                label="Infrastructure Checks (roofing, fencing, lighting, gas safety, etc.)"
                options={infrastructureAreasOptions || []}
                formik_props={formik_props}
                disabled={fieldsDisabled}
              />
              <Typography className={classes.fieldDescription} variant="caption" display="block">
                Tick all items that are present and in safe working order.
              </Typography>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={expanded === 'accessSecurity'}
        onChange={handleChange('accessSecurity')}
        variant="outlined"
        square
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="access-security-content" id="access-security-header">
          <Typography className={classes.heading}>Access & Security</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormikSwitchField
                name="company.has_access_for_delivery"
                label="Ease of Access for Delivery"
                formik_props={formik_props}
                color="primary"
                checked={get(company, 'has_access_for_delivery')}
                disabled={fieldsDisabled}
              />
              <Typography className={classes.fieldDescription} variant="caption" display="block">
                Confirm whether delivery vehicles can access the site without difficulty.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormikSwitchField
                name="company.has_parking_security"
                label="Parking & Security"
                formik_props={formik_props}
                color="primary"
                checked={get(company, 'has_parking_security')}
                disabled={fieldsDisabled}
              />
              <Typography className={classes.fieldDescription} variant="caption" display="block">
                Confirm parking availability and basic site security measures.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormikSwitchField
                name="company.has_proximity_to_police"
                label="Proximity to Police Station"
                formik_props={formik_props}
                color="primary"
                checked={get(company, 'has_proximity_to_police')}
                disabled={fieldsDisabled}
              />
              <Typography className={classes.fieldDescription} variant="caption" display="block">
                State whether the site is near a police station (for safety assessment).
              </Typography>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </>
  )
}
